import AutoScroll from 'embla-carousel-auto-scroll';
import { useState } from 'react';

import { cn } from '@/lib/utils';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/components/ui/carousel';
import { useTheme } from '@/context/ThemeContext';

interface Logo {
  name: string;
  image?: string;
  /** Apply brightness(0) invert(1) to make dark logos visible on dark backgrounds */
  invert?: boolean;
}

interface Logos3Props {
  heading?: string;
  logos?: Logo[];
  className?: string;
}

const defaultLogos: Logo[] = [
  { name: 'Toyota', image: '/logos/toyota.png' },
  { name: 'Techspan Engineering', image: '/logos/techspan-engineering.jpg' },
  { name: 'PRMI', image: '/logos/prmi.jpeg' },
  { name: 'Drishti', image: '/logos/drishti.png' },
  { name: 'Microsoft', image: '/logos/microsoft.png' },
  { name: 'PepsiCo', image: '/logos/pepsico.svg' },
  { name: 'MYK Laticrete', image: '/logos/myk-laticrete.png' },
  { name: 'New Home Lending Co.', image: '/logos/newhomelendingco-logo.png' },
  { name: 'Zyter TruCare', image: '/logos/zyter-trucare.png' },
  { name: 'Databricks', image: '/logos/databricks.png' },
  { name: 'Agno', image: '/logos/agno.png' },
  { name: 'Fleetguard', image: '/logos/fleetguard.svg', invert: true },
  { name: 'Evergreen Home Loans', image: '/logos/evergreen-home-loans.png' },
];

const LogoItem = ({ logo }: { logo: Logo }) => {
  const [imgError, setImgError] = useState(false);
  const { resolved } = useTheme();
  const isDark = resolved === 'dark';

  if (!logo.image || imgError) {
    return (
      <span className="text-sm md:text-base font-semibold text-foreground/60 whitespace-nowrap tracking-wide">
        {logo.name}
      </span>
    );
  }

  return (
    <img
      src={logo.image}
      alt={logo.name}
      className={cn(
        'h-8 md:h-10 w-auto object-contain',
        isDark && logo.invert && 'brightness-0 invert',
      )}
      onError={() => setImgError(true)}
    />
  );
};

const Logos3 = ({
  heading = 'Trusted by industry leaders',
  logos = defaultLogos,
  className,
}: Logos3Props) => {
  return (
    <section className={cn('container mx-auto px-4 mb-24 section-glow', className)}>
      <div className="text-center mb-14 max-w-2xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-semibold mb-4 tracking-[-0.04em]">
          {heading}
        </h2>
      </div>
      <div className="relative mx-auto max-w-5xl">
        <div className="pointer-events-none absolute left-0 top-0 z-10 h-full w-24 bg-gradient-to-r from-background to-transparent" />
        <div className="pointer-events-none absolute right-0 top-0 z-10 h-full w-24 bg-gradient-to-l from-background to-transparent" />
        <Carousel
          opts={{ loop: true }}
          plugins={[
            AutoScroll({ playOnInit: true, speed: 0.7, stopOnInteraction: false }),
          ]}
        >
          <CarouselContent className="ml-0">
            {logos.map((logo, idx) => (
              <CarouselItem
                key={idx}
                className="basis-1/3 sm:basis-1/4 md:basis-1/5 lg:basis-1/6 pl-8 flex items-center justify-center py-6"
              >
                <LogoItem logo={logo} />
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </div>
    </section>
  );
};

export { Logos3 };
